import { DeviceConfigurationFields } from '../API/types/Devices';

/**
 * Shown when `GET /device-config/defaults` returns nothing — the service is older than this build,
 * or the request failed.
 *
 * The config forms cannot render without it: they seed every field from the service defaults and
 * fall back to them for keys a device or fleet does not carry. Rendering anyway would give a form
 * with no values, and rendering nothing at all is worse still — it looks like the settings vanished,
 * which is exactly what happened before this message existed. So the forms say so explicitly.
 */
export const DEFAULTS_UNAVAILABLE_MESSAGE = 'Default configuration could not be loaded from the service, so this form cannot be shown. Reload the page to retry.';

/**
 * Human labels for the per-installation calibration keys a cloud reset preserves. The API reports
 * the actual keys in `preservedKeys`; anything without a label here falls back to the raw key.
 * Wording follows the HW Configuration form so the same parameter reads the same in both places.
 */
export const PRESERVED_CONFIG_KEY_LABELS: Record<string, string> = {
  'config/E3D/zone_calibration/m4': 'Distance to the top edge of the windshield',
  'config/E3D/zone_calibration/m3': 'Distance to the bottom edge of the windshield',
  'config/E3D/zone_calibration/m2': 'Distance to the right edge of the windshield',
  'config/E3D/zone_calibration/m1': 'Distance to the left edge of the windshield',
  'config/E3D/zone_calibration/pitch': 'Interior camera pitch angle',
  'config/E3D/zone_calibration/yaw': 'Interior camera yaw angle',
  'config/E3D/zone_calibration/d': 'Distance from camera to the windshield',
  'config/E3D/zone_calibration/blockage_leftColumnWidth': 'Interior camera blockage region width (left)',
  'config/E3D/zone_calibration/blockage_rightColumnWidth': 'Interior camera blockage region width (right)',
  'config/HA/extrinsics/camera_offset_x': 'Exterior camera offset X',
  'config/HA/extrinsics/camera_offset_y': 'Exterior camera offset Y',
  'config/HA/extrinsics/camera_offset_z': 'Exterior camera offset Z',
  'config/HA/extrinsics/camera_yaw': 'Exterior camera yaw rotation',
  'config/HA/extrinsics/camera_pitch': 'Exterior camera pitch rotation',
  'config/HA/extrinsics/camera_roll': 'Exterior camera roll rotation',
  'config/HA/general/vehicle_length': 'Vehicle length',
  'config/HA/general/vehicle_width': 'Vehicle width',
};

/**
 * The value a field would revert to if the user hit "Reset to Default", for labelling that field.
 *
 * A reset writes back the config file uploaded for the device's HW model and FW version, so that
 * file wins wherever it carries the key. It need not carry every parameter though, and it is not
 * loaded yet on first render, so the service's built-in defaults fill the gaps — a label is never
 * left blank, and never shows a value the reset would not actually write.
 */
export const resolveDefaultHint = (
  key: string,
  resetDefaults: object | undefined,
  builtInDefaults: object | undefined,
): string => {
  // Both sources are keyed by config path, but DeviceConfigurationFields is an interface and so
  // carries no index signature — read them through one narrowing point rather than at every site.
  const read = (source: object | undefined): unknown => (
    (source as Record<string, unknown> | undefined)?.[key]
  );

  const value = read(resetDefaults) ?? read(builtInDefaults);

  return value === undefined || value === null ? '' : String(value);
};

type DeviceConfigurationInput = DeviceConfigurationFields | Record<string, boolean | number | string>;

const LEGACY_DEVICE_CONFIGURATION_KEY_MAP: Record<string, keyof DeviceConfigurationFields> = {
  'config/IMU/power_supply_cut/enabled': 'config/WIRE-CUT/wire_cut/enabled',
  'config/IMU/power_supply_cut/active_warning': 'config/WIRE-CUT/wire_cut/active_warning',
};

const coerceConfigurationValue = (value: unknown): boolean | number | null => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }

  if (typeof value === 'string') {
    if (value.trim() === '') {
      return null;
    }

    const numericValue = Number(value);
    if (!Number.isNaN(numericValue)) {
      return numericValue;
    }
  }

  return null;
};

/**
 * Fills a partial configuration out to a complete one, falling back to the service defaults for
 * anything the device or fleet does not carry, and migrating the legacy power-supply-cut keys.
 *
 * `defaults` comes from `useGetDeviceDefaultConfiguration` (GET /device-config/defaults) rather than
 * a constant in this repo — the portal used to keep its own copy, and it silently fell five
 * parameters behind the service. Passing them in also makes the set of accepted keys follow the
 * service: a parameter the service adds shows up here as soon as it is deployed.
 */
export const normalizeDeviceConfiguration = (
  defaults: DeviceConfigurationFields,
  configuration?: DeviceConfigurationInput,
): DeviceConfigurationFields => {
  const validKeys = new Set(Object.keys(defaults));
  const normalized = { ...defaults } as DeviceConfigurationFields;

  if (!configuration) {
    return normalized;
  }

  Object.entries(configuration).forEach(([key, value]) => {
    const coercedValue = coerceConfigurationValue(value);
    if (validKeys.has(key) && coercedValue !== null) {
      normalized[key as keyof DeviceConfigurationFields] = coercedValue as never;
    }
  });

  Object.entries(configuration).forEach(([key, value]) => {
    const migratedKey = LEGACY_DEVICE_CONFIGURATION_KEY_MAP[key];
    const coercedValue = coerceConfigurationValue(value);
    if (
      migratedKey
      && !Object.prototype.hasOwnProperty.call(configuration, migratedKey)
      && coercedValue !== null
    ) {
      normalized[migratedKey] = coercedValue as never;
    }
  });

  return normalized;
};
